import React from 'react';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import FilterDropdown, { type FilterSection } from '../../components/FilterDropdown';
import ActiveFilterChips, { type FilterChip } from '../../components/ActiveFilterChips';
import { BANDWIDTH_CATEGORIES, type BandwidthCategoryId } from './types';

export interface BandwidthFilterBarProps {
  activeCategoryId: BandwidthCategoryId;
  onCategoryChange: (id: BandwidthCategoryId) => void;
  sections: FilterSection[];
  values: Record<string, string | string[]>;
  onFilterChange: (sectionId: string, value: string | string[]) => void;
  chips: FilterChip[];
  onRemoveChip: (chipId: string) => void;
  onClearAllFilters: () => void;
}

const BandwidthFilterBar: React.FC<BandwidthFilterBarProps> = ({
  activeCategoryId,
  onCategoryChange,
  sections,
  values,
  onFilterChange,
  chips,
  onRemoveChip,
  onClearAllFilters,
}) => {
  const activeLabel =
    BANDWIDTH_CATEGORIES.find((c) => c.id === activeCategoryId)?.label ?? 'Dashboard';

  return (
    <div className="relative z-50 mx-4 mt-4 shrink-0 sm:mx-5">
      <div className="rounded-xl border border-indigo-100/70 bg-white shadow-md shadow-indigo-900/10 ring-1 ring-slate-950/[0.04]">
        <div className="h-1 rounded-t-xl bg-gradient-to-r from-indigo-500 via-violet-500 to-sky-500" aria-hidden />
        <div className="px-4 py-3.5 sm:px-5">
          <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3 sm:gap-4">
              <Menu as="div" className="relative inline-block text-left">
                <Menu.Button className="inline-flex min-w-[200px] items-center gap-2 rounded-xl border border-indigo-100 bg-white px-4 py-2.5 text-left text-sm font-semibold text-slate-900 shadow-sm transition-all hover:border-indigo-200 hover:bg-gradient-to-r hover:from-slate-50/80 hover:to-indigo-50/40 focus:outline-none focus:ring-2 focus:ring-[#4472C4] focus:ring-offset-2 sm:min-w-[240px]">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                    <ChartBarIcon className="h-4 w-4 shrink-0" aria-hidden />
                  </span>
                  <span className="truncate">{activeLabel}</span>
                  <ChevronDownIcon className="ml-auto h-5 w-5 shrink-0 text-slate-400" aria-hidden />
                </Menu.Button>
                <Transition
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute left-0 z-[60] mt-2 w-64 origin-top-left rounded-xl border border-slate-200/80 bg-white py-1 shadow-xl shadow-slate-900/10 ring-1 ring-slate-950/5 focus:outline-none">
                    <div className="border-b border-slate-100 px-3 py-2.5">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Category</p>
                    </div>
                    {BANDWIDTH_CATEGORIES.map((cat) => (
                      <Menu.Item key={cat.id}>
                        {({ active }) => (
                          <button
                            type="button"
                            onClick={() => onCategoryChange(cat.id)}
                            className={`flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors ${
                              activeCategoryId === cat.id
                                ? 'bg-indigo-50 font-semibold text-indigo-800'
                                : active
                                  ? 'bg-slate-50 text-slate-900'
                                  : 'text-slate-700'
                            }`}
                          >
                            {activeCategoryId === cat.id && (
                              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-600" aria-hidden />
                            )}
                            <span className={activeCategoryId === cat.id ? 'font-semibold' : ''}>{cat.label}</span>
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                  </Menu.Items>
                </Transition>
              </Menu>
              <FilterDropdown sections={sections} values={values} onChange={onFilterChange} />
            </div>
          </div>
        </div>
        <div className="rounded-b-xl border-t border-indigo-100/50 bg-gradient-to-r from-slate-50/80 via-white to-violet-50/25 px-4 py-3 sm:px-5">
          <ActiveFilterChips chips={chips} onRemove={onRemoveChip} onClearAll={onClearAllFilters} />
        </div>
      </div>
    </div>
  );
};

export default BandwidthFilterBar;
